import Type from "typebox";

import { ApiKeyScopeSchema, ResourceBoundarySchema, type ApiKeyScope } from "./authorization.js";
import {
  DateTimeSchema,
  OneTimeSecretSchema,
  RequestIdSchema,
  RevisionSchema,
  SafeLabelSchema,
} from "./common.js";
import { PageInfoSchema } from "./pagination.js";
import {
  ApiKeyIdSchema,
  ReauthenticationIdSchema,
  TenantIdSchema,
  UserIdSchema,
} from "./resources.js";

interface ScopeDefinition {
  readonly label: string;
  readonly description: string;
  readonly dangerous: boolean;
}

export const API_KEY_SCOPE_DEFINITIONS = {
  "system:read": {
    label: "Sistem durumunu görüntüle",
    description: "Platform sağlık ve yetenek bilgilerini okur.",
    dangerous: false,
  },
  "audit:read": {
    label: "Denetim kaydını görüntüle",
    description: "Yetkili denetim kayıtlarını okur.",
    dangerous: false,
  },
  "projects:read": {
    label: "Projeleri görüntüle",
    description: "İzin verilen proje bilgilerini okur.",
    dangerous: false,
  },
  "hosts:read": {
    label: "Sunucuları görüntüle",
    description: "İzin verilen sunucu durumlarını okur.",
    dangerous: false,
  },
  "deployments:read": {
    label: "Dağıtımları görüntüle",
    description: "Dağıtım ve release durumlarını okur.",
    dangerous: false,
  },
  "deployments:create": {
    label: "Dağıtım başlat",
    description: "Sınır içindeki projelerde yeni dağıtım başlatır.",
    dangerous: false,
  },
  "deployments:cancel": {
    label: "Dağıtımı iptal et",
    description: "İptal edilebilir dağıtım işlerini durdurur.",
    dangerous: true,
  },
  "deployments:rollback": {
    label: "Rollback başlat",
    description: "Tutulan bir release için yeni rollback işlemi başlatır.",
    dangerous: true,
  },
  "jobs:read": {
    label: "İşleri görüntüle",
    description: "Asenkron iş durumlarını ve olaylarını okur.",
    dangerous: false,
  },
  "jobs:cancel": {
    label: "İşi iptal et",
    description: "İptal edilebilir asenkron işleri durdurur.",
    dangerous: true,
  },
  "logs:read": {
    label: "Logları görüntüle",
    description: "Erişim sınırındaki redakte edilmiş logları okur.",
    dangerous: false,
  },
  "config:read": {
    label: "Yapılandırmayı görüntüle",
    description: "Gizli değerler hariç yapılandırma durumunu okur.",
    dangerous: false,
  },
  "config:write": {
    label: "Yapılandırmayı değiştir",
    description: "Gizli olmayan yapılandırmayı değiştirir.",
    dangerous: true,
  },
  "secrets:write": {
    label: "Gizli değer yaz",
    description: "Gizli değer oluşturur veya değiştirir; açık değeri okuyamaz.",
    dangerous: true,
  },
  "domains:read": {
    label: "Domainleri görüntüle",
    description: "Domain, DNS ve TLS durumunu okur.",
    dangerous: false,
  },
  "domains:write": {
    label: "Domainleri değiştir",
    description: "Yetkili domain ve DNS planlarını uygular.",
    dangerous: true,
  },
  "databases:read": {
    label: "Veritabanlarını görüntüle",
    description: "Yönetilen veritabanı durumunu okur.",
    dangerous: false,
  },
  "databases:operate": {
    label: "Veritabanını işlet",
    description: "Başlatma, durdurma ve yeniden başlatma işlemlerini yürütür.",
    dangerous: true,
  },
  "databases:backup": {
    label: "Yedek oluştur",
    description: "Yönetilen veritabanı yedeği başlatır.",
    dangerous: false,
  },
  "databases:restore": {
    label: "Yedek geri yükle",
    description: "Açıkça seçilen yedeği kontrollü bir işlemle geri yükler.",
    dangerous: true,
  },
} as const satisfies Record<ApiKeyScope, ScopeDefinition>;

export const API_KEY_PRESET_CODES = ["observe", "deploy", "operate", "custom"] as const;

export const ApiKeyPresetSchema = Type.Union([
  Type.Literal("observe"),
  Type.Literal("deploy"),
  Type.Literal("operate"),
  Type.Literal("custom"),
]);

export type ApiKeyPreset = Type.Static<typeof ApiKeyPresetSchema>;

export const API_KEY_PRESET_SCOPES = {
  observe: [
    "system:read",
    "projects:read",
    "hosts:read",
    "deployments:read",
    "jobs:read",
    "logs:read",
  ],
  deploy: [
    "system:read",
    "projects:read",
    "hosts:read",
    "deployments:read",
    "deployments:create",
    "deployments:cancel",
    "jobs:read",
    "logs:read",
  ],
  operate: [
    "system:read",
    "projects:read",
    "hosts:read",
    "deployments:read",
    "deployments:create",
    "deployments:cancel",
    "jobs:read",
    "jobs:cancel",
    "logs:read",
    "config:read",
    "config:write",
  ],
  custom: [],
} as const satisfies Record<ApiKeyPreset, readonly ApiKeyScope[]>;

export const API_KEY_STATUSES = ["active", "expiring", "expired", "revoked"] as const;

export const ApiKeyStatusSchema = Type.Union([
  Type.Literal("active"),
  Type.Literal("expiring"),
  Type.Literal("expired"),
  Type.Literal("revoked"),
]);

export const ApiKeySchema = Type.Object(
  {
    id: ApiKeyIdSchema,
    tenantId: TenantIdSchema,
    label: SafeLabelSchema,
    preset: ApiKeyPresetSchema,
    scopes: Type.Array(ApiKeyScopeSchema, { minItems: 1, uniqueItems: true }),
    resourceBoundary: ResourceBoundarySchema,
    status: ApiKeyStatusSchema,
    createdByUserId: UserIdSchema,
    createdAt: DateTimeSchema,
    expiresAt: DateTimeSchema,
    lastUsedAt: Type.Optional(DateTimeSchema),
    revokedAt: Type.Optional(DateTimeSchema),
    revision: RevisionSchema,
  },
  {
    $id: "ApiKey",
    additionalProperties: false,
    description: "Persistent API-key metadata. No token value or authentication prefix is exposed.",
  },
);

export const CreateApiKeyRequestSchema = Type.Object(
  {
    label: SafeLabelSchema,
    preset: ApiKeyPresetSchema,
    scopes: Type.Array(ApiKeyScopeSchema, { minItems: 1, uniqueItems: true }),
    resourceBoundary: ResourceBoundarySchema,
    expiresAt: DateTimeSchema,
  },
  {
    $id: "CreateApiKeyRequest",
    additionalProperties: false,
  },
);

export const CreateApiKeyResponseSchema = Type.Object(
  {
    requestId: RequestIdSchema,
    apiKey: ApiKeySchema,
    secret: OneTimeSecretSchema,
  },
  {
    $id: "CreateApiKeyResponse",
    additionalProperties: false,
    description: "Creation-only response. The secret cannot be fetched again.",
  },
);

export const ApiKeysResponseSchema = Type.Object(
  {
    items: Type.Array(ApiKeySchema),
    page: PageInfoSchema,
  },
  {
    $id: "ApiKeysResponse",
    additionalProperties: false,
  },
);

export const RevokeApiKeyRequestSchema = Type.Object(
  {
    confirmationLabel: SafeLabelSchema,
    reauthenticationId: ReauthenticationIdSchema,
  },
  {
    $id: "RevokeApiKeyRequest",
    additionalProperties: false,
  },
);

export const RevokeApiKeyResponseSchema = Type.Object(
  {
    requestId: RequestIdSchema,
    apiKeyId: ApiKeyIdSchema,
    revokedAt: DateTimeSchema,
  },
  {
    $id: "RevokeApiKeyResponse",
    additionalProperties: false,
  },
);

export type ApiKeyStatus = Type.Static<typeof ApiKeyStatusSchema>;
export type ApiKey = Type.Static<typeof ApiKeySchema>;
export type CreateApiKeyRequest = Type.Static<typeof CreateApiKeyRequestSchema>;
export type CreateApiKeyResponse = Type.Static<typeof CreateApiKeyResponseSchema>;
export type RevokeApiKeyRequest = Type.Static<typeof RevokeApiKeyRequestSchema>;
